import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  format,
  addDays,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  isSameMonth,
  isSameDay,
  addMonths,
  subMonths,
} from 'date-fns';

import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import AddCircleIcon from '@mui/icons-material/AddCircle';

import MonthPicker from './MonthPicker';
import RestrictComponent from '../shared/RestrictComponent';

import { toggleSidebar } from '../../actions/sidebarActions';

import { userRoles } from '../../constants';

import './calendar.scss';

const Calendar = ({ selectedDay, onDaySelect, toggleSidebar, cases, handleAddClick, month, setMonth }) => {
  const { GLOBAL_ADMIN, COUNTRY_ADMIN, SURGEON, PRACTICE_MANAGER, SALES_REP } = userRoles;

  const renderDays = () => {
    const dateFormat = 'EEE';
    const days = [];

    let startDate = startOfWeek(month);

    for (let i = 0; i < 7; i++) {
      days.push(
        <div className="days-cell" key={i}>
          {format(addDays(startDate, i), dateFormat)}
        </div>
      );
    }

    return <div className="days-row">{days}</div>;
  };

  const renderRows = () => {
    const monthStart = startOfMonth(month);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);

    const rows = [];

    let days = [];
    let day = startDate;
    let tabIndex = 0;

    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        days.push(renderCell(day, tabIndex));
        day = addDays(day, 1);
        tabIndex++;
      }
      rows.push(
        <div key={day} className="calendar-row">
          {days}
        </div>
      );
      days = [];
    }

    return <div className="calendar-rows">{rows}</div>;
  };

  const renderCell = (day) => {
    const disabled = isDisabled(day);
    const dayCases = cases.filter((item) => isSameDay(day, item.date));
    const requiredAction = !!dayCases.length && dayCases.filter((item) => item.access);
    const selected = isSameDay(day, selectedDay);
    const isToday = isSameDay(day, new Date());

    return (
      <div
        key={day}
        className={`calendar-cell ${disabled ? 'disabled' : ''} ${selected ? 'selected' : ''} ${
          isToday ? 'today' : ''
        }`}
        onClick={() => {
          if (!disabled) {
            onDaySelect(day, dayCases);
          }
        }}
      >
        <div className="d-flex space-between align-start">
          <div className="cell-day">{format(day, 'd')}</div>
          {!!dayCases.length && <div className="cases-indicator" />}
        </div>
        {selected && !disabled && (
          <div className="cell-info">
            {!!dayCases.length ? (
              <div>
                <div>{dayCases.length === 1 ? '1 case' : `${dayCases.length} cases`}</div>
                <div>{requiredAction.length} action required</div>
              </div>
            ) : (
              'No cases'
            )}
          </div>
        )}
      </div>
    );
  };

  const isDisabled = (day) => {
    const monthStart = startOfMonth(month);

    return !isSameMonth(day, monthStart);
  };

  const nextMonth = () => {
    const nextMonth = addMonths(month, 1);

    setMonth(nextMonth);
  };

  const prevMonth = () => {
    const prevMonth = subMonths(month, 1);

    setMonth(prevMonth);
  };

  return (
    <div className="calendar-container">
      <Paper className="calendar-paper">
        <div className="calendar-header">
          <div className="d-flex">
            {/* Mobile menu button */}
            <Box sx={{ display: { sm: 'none' } }}>
              <div className="m-r-sm">
                <IconButton
                  edge="start"
                  color="primary"
                  aria-label="menu"
                  onClick={toggleSidebar}
                >
                  <MenuIcon />
                </IconButton>
              </div>
            </Box>

            <div className="font-size-lg">Calendar</div>
          </div>

          {/* Desktop month picker */}
          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
            <MonthPicker
              date={month}
              onChange={setMonth}
              prevMonth={prevMonth}
              nextMonth={nextMonth}
            />
          </Box>

          <RestrictComponent
            roles={[SURGEON, PRACTICE_MANAGER, GLOBAL_ADMIN, SALES_REP, COUNTRY_ADMIN]}
          >
            <IconButton
              edge="start"
              color="primary"
              aria-label="add"
              onClick={handleAddClick}
            >
              <AddCircleIcon />
            </IconButton>
          </RestrictComponent>
        </div>

        {/* Mobile month picker */}
        <Box sx={{ display: { sm: 'none' } }}>
          <MonthPicker
            date={month}
            onChange={setMonth}
            prevMonth={prevMonth}
            nextMonth={nextMonth}
          />
        </Box>

        <div className="calendar">
          {renderDays()}
          {renderRows()}
        </div>
      </Paper>
    </div>
  );
};

Calendar.propTypes = {
  selectedDay: PropTypes.instanceOf(Date),
  cases: PropTypes.array,
  onDaySelect: PropTypes.func,
  toggleSidebar: PropTypes.func,
  handleAddClick: PropTypes.func,
};

const mapDispatchToProps = (dispatch) => {
  return {
    toggleSidebar: () => dispatch(toggleSidebar()),
  };
};

export default connect(null, mapDispatchToProps)(Calendar);
